import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import useManufacturers from "@/hooks/useManufacturers";
import useProductTypes, { invalidateProductTypes } from "@/hooks/useProductTypes";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Tags, Pencil, Trash2 } from "lucide-react";

function TypeDialog({ open, onOpenChange, initial, defaultCompanyId, onSaved }) {
  const mfrs = useManufacturers();
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initial ? { ...initial } : { manufacturer_id: defaultCompanyId || "", is_active: true });
  }, [open, initial, defaultCompanyId]);

  const submit = async () => {
    if (!(form.name || "").trim()) { toast.error("Type name required"); return; }
    if (!form.manufacturer_id) { toast.error("Pick a company — product types belong to a company"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        manufacturer_id: form.manufacturer_id,
        description: form.description || null,
        display_order: Number(form.display_order) || 0,
        is_active: form.is_active !== false,
      };
      if (isEdit) await api.patch(`/product-types/${initial.id}`, payload);
      else await api.post("/product-types", payload);
      toast.success(isEdit ? "Product type updated" : "Product type created");
      invalidateProductTypes();
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="product-type-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">{isEdit ? "Edit product type" : "New product type"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold">Company *</Label>
            <Select value={form.manufacturer_id || "__none"} onValueChange={(v) => setForm((f) => ({ ...f, manufacturer_id: v === "__none" ? "" : v }))}>
              <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="type-company-select"><SelectValue placeholder="Pick" /></SelectTrigger>
              <SelectContent className="rounded-2xl">
                <SelectItem value="__none">— Select —</SelectItem>
                {mfrs.filter((m) => m.is_active !== false).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold">Type name *</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.name || ""} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Acoutherm Thermal" data-testid="type-name-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Description</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.description || ""} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-2xl" data-testid="type-save-button">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const ProductTypeManager = ({ canManage }) => {
  const mfrs = useManufacturers();
  const types = useProductTypes();
  const [companyFilter, setCompanyFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const mfrById = mfrs.reduce((a, m) => { a[m.id] = m; return a; }, {});
  const filtered = types.filter((t) => !companyFilter || t.manufacturer_id === companyFilter);

  const remove = async (t) => {
    if (!window.confirm(`Delete product type "${t.name}"?\n\nIf it's in use it will be deactivated instead.`)) return;
    try {
      const { data } = await api.delete(`/product-types/${t.id}`);
      toast.success(data.deactivated ? "Type deactivated (in use by items)" : "Type deleted");
      invalidateProductTypes();
    } catch (err) { toast.error(fmtError(err)); }
  };

  return (
    <div data-testid="product-type-manager">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select value={companyFilter || "__all"} onValueChange={(v) => setCompanyFilter(v === "__all" ? "" : v)}>
          <SelectTrigger className="rounded-2xl h-10 w-[200px]" data-testid="type-manager-company-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-2xl">
            <SelectItem value="__all">All companies</SelectItem>
            {mfrs.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {canManage && (
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-2xl ml-auto" data-testid="new-product-type-button">
            <Plus className="h-4 w-4 mr-1" /> New type
          </Button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {filtered.map((t) => (
          <Card key={t.id} className={`rounded-3xl border-border/60 shadow-soft ${t.is_active === false ? "opacity-60" : ""}`} data-testid={`product-type-card-${t.id}`}>
            <CardContent className="p-4 flex items-start gap-3">
              <div className="h-10 w-10 rounded-2xl bg-primary-50 text-primary-700 grid place-items-center shrink-0">
                <Tags className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-extrabold truncate">{t.name}</p>
                <p className="text-xs text-muted-foreground truncate">{mfrById[t.manufacturer_id]?.name || "—"}</p>
                {t.is_active === false && <Badge variant="outline" className="text-[10px] mt-1 bg-stone-100 text-stone-500 border-stone-200">Inactive</Badge>}
              </div>
              {canManage && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" className="rounded-xl h-8 w-8 p-0" onClick={() => { setEditing(t); setOpen(true); }} data-testid={`type-edit-${t.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" className="rounded-xl h-8 w-8 p-0 text-red-600 hover:bg-red-50" onClick={() => remove(t)} data-testid={`type-delete-${t.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && (
          <Card className="rounded-3xl border-border/60 shadow-soft sm:col-span-2">
            <CardContent className="p-10 text-center text-sm text-muted-foreground">
              No product types yet{companyFilter ? " for this company" : ""}. Create types (e.g. "Acoutherm Thermal", "Acoustic") and assign them to products, services and trainings.
            </CardContent>
          </Card>
        )}
      </div>

      <TypeDialog open={open} onOpenChange={setOpen} initial={editing} defaultCompanyId={companyFilter} onSaved={() => {}} />
    </div>
  );
};
